import React from 'react';
import { motion } from 'framer-motion';
import { Code2, Smartphone, Search, Gauge, FileCode, Package, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const icons = [Code2, Smartphone, Search, Gauge, FileCode, Package];
const gradients = [
  'from-violet-500 to-purple-600',
  'from-blue-500 to-cyan-500',
  'from-green-500 to-emerald-500',
  'from-yellow-500 to-orange-500',
  'from-pink-500 to-rose-500',
  'from-indigo-500 to-blue-500'
];

export function WhatYouGet() {
  const { t } = useLanguage();
  const items = t('whatYouGet.items');

  return (
    <section id="solutions" className="py-24 md:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
      
      {/* Grid pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'linear-gradient(rgba(99, 102, 241, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(99, 102, 241, 0.5) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-mono text-sm tracking-wider uppercase mb-4 block">
            The package
          </span>
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-4">
            {t('whatYouGet.title')}
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-100px' }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div 
                  className="relative bg-slate-900/50 border border-slate-800 backdrop-blur-xl p-8 rounded-2xl overflow-hidden hover:border-primary/50 transition-all duration-500 hover:-translate-y-2 h-full"
                  data-testid={`feature-card-${index}`}
                >
                  {/* Hover gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradients[index]} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                  
                  {/* Number indicator */}
                  <div className="absolute top-4 right-4 font-mono text-4xl font-bold text-slate-800 group-hover:text-slate-700 transition-colors">
                    0{index + 1}
                  </div>

                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradients[index]} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="text-white" size={24} />
                  </div>
                  
                  {/* Content */}
                  <h3 className="font-heading text-xl font-bold mb-3 flex items-center gap-2 pr-10">
                    {item.title}
                    <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity text-primary" size={18} />
                  </h3>
                  <p className="text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
